import { STORAGE_KEY_ANON_ID } from './constants'
import type { StorageAdapter } from './storage'
import { createDefaultStorage } from './storage'
import type { UserIdentity } from './types'
import { generateId, isBrowser } from './utils'

export class IdentityManager {
  private anonId: string
  private userId: string | undefined
  private sessionId: string
  private traits: Record<string, unknown> = {}
  private superProps: Record<string, unknown> = {}
  private storage: StorageAdapter

  constructor(storage?: StorageAdapter) {
    this.storage = storage ?? createDefaultStorage()

    // Cross-site tracking: URL의 podo_anon_id 파라미터를 우선 사용
    const crossSiteAnonId = this.getCrossSiteAnonId()
    if (crossSiteAnonId) {
      this.anonId = crossSiteAnonId
      this.storage.setItem(STORAGE_KEY_ANON_ID, crossSiteAnonId)
    } else {
      const stored = this.storage.getItem(STORAGE_KEY_ANON_ID)
      if (stored) {
        this.anonId = stored
      } else {
        this.anonId = generateId()
        this.storage.setItem(STORAGE_KEY_ANON_ID, this.anonId)
      }
    }

    this.sessionId = generateId()
  }

  private getCrossSiteAnonId(): string | null {
    if (!isBrowser()) {
      return null
    }
    try {
      const params = new URLSearchParams(window.location.search)
      return params.get(STORAGE_KEY_ANON_ID)
    } catch {
      return null
    }
  }

  getAnonId(): string {
    return this.anonId
  }

  getUserId(): string | undefined {
    return this.userId
  }

  getSessionId(): string {
    return this.sessionId
  }

  getTraits(): Record<string, unknown> {
    return { ...this.traits }
  }

  getSuperProperties(): Record<string, unknown> {
    return { ...this.superProps }
  }

  identify(userId: string, traits?: Record<string, unknown>): void {
    this.userId = userId
    if (traits) {
      this.traits = { ...this.traits, ...traits }
    }
  }

  setUser(identity: UserIdentity): void {
    this.userId = identity.userId
    if (identity.anonId) {
      this.anonId = identity.anonId
      this.storage.setItem(STORAGE_KEY_ANON_ID, this.anonId)
    }
    if (identity.traits) {
      this.traits = { ...this.traits, ...identity.traits }
    }
  }

  setSuperProperties(props: Record<string, unknown>): void {
    this.superProps = { ...this.superProps, ...props }
  }

  reset(): void {
    this.userId = undefined
    this.traits = {}
    this.superProps = {}
    this.anonId = generateId()
    this.sessionId = generateId()
    this.storage.setItem(STORAGE_KEY_ANON_ID, this.anonId)
  }
}
